/**
 * platform/ui/src/components/CriteriaForm.js
 *
 * Phase 1 PATCH (on top of previous):
 *   1. Add 'bloodPoolROINames' (array-of-strings, edited as one-per-line text)
 *      and 'borderlineRatio' (percent fraction) to the value/handler switches.
 *   2. Render both in the RECIP-specific section.
 *
 * Conversion conventions:
 *   - bloodPoolROINames stored as string[]; rendered as one-per-line text.
 *     getCriterionValue: array → "a\nb\nc" string
 *     onCriterionChange: text → array, trimmed and de-empty'd
 *   - borderlineRatio stored as fraction (0.15); rendered as 15 (%).
 *     Same idiom as minVolumeIncrease / minVolumeDecrease.
 *
 * Everything else preserved.
 */

import React, { useState } from 'react';
import { Typography, Box, Grid, Divider, Button } from '@material-ui/core';
import _ from 'lodash';

import { guidelines } from '@platform/core';
import { Body, Container } from '@platform/ui';
import CriterionBox from './CriterionBox';
import SuvDefaultsPanel from './SuvDefaultsPanel';

const CriteriaForm = ({
  defaultCriteria,
  onUpdateCriteriaClick,
  timepoint = {},
  component = 'Platform',
  readOnly = false,
}) => {
  const [criteria, setCriteria] = useState(defaultCriteria);

  const getCriterionValue = (key, value) => {
    switch (key) {
      case 'guidelineId':
      case 'measurementTechnique':
      case 'completeResponseConfirmation':
      case 'newLesionConfirmation':
      case 'newLesionDateOfProgression':
      case 'newLesionAlert':
      case 'overallResponseAlert':
      case 'pdReference':
      case 'newLesionAccounting':
      case 'bloodPoolSuvVariant':
        return value || '';
      case 'minDiameterIncrease':
      case 'minDiameterDecrease':
      case 'minVolumeIncrease':
      case 'minVolumeDecrease':
      /* --------- NEW: percent fraction --------- */
      case 'borderlineRatio':
        /* ----------------------------------------- */
        return Math.round(100 * Number(value || 0));
      /* --------- NEW: array → newline-joined text --------- */
      case 'bloodPoolROINames':
        return Array.isArray(value) ? value.join('\n') : value || '';
      /* ---------------------------------------------------- */
      default:
        return Number(value || 0);
    }
  };

  const onCriterionChange = (key, value) => {
    let newCriteria = _.cloneDeep(criteria);
    switch (key) {
      case 'guidelineId':
        if (value === 'CUSTOM') {
          newCriteria = _.cloneDeep(defaultCriteria);
          newCriteria['guidelineId'] = value;
          newCriteria['guidelineName'] = guidelines[value].guidelineName;
        } else {
          newCriteria = guidelines[value];
        }
        break;
      case 'measurementTechnique':
      case 'completeResponseConfirmation':
      case 'newLesionConfirmation':
      case 'newLesionDateOfProgression':
      case 'newLesionAlert':
      case 'overallResponseAlert':
      case 'pdReference':
      case 'newLesionAccounting':
      case 'bloodPoolSuvVariant':
        newCriteria[key] = value;
        break;
      case 'minDiameterIncrease':
      case 'minDiameterDecrease':
      case 'minVolumeIncrease':
      case 'minVolumeDecrease':
      /* --------- NEW: percent → fraction --------- */
      case 'borderlineRatio':
        /* ------------------------------------------- */
        newCriteria[key] = Number(value) / 100;
        break;
      /* --------- NEW: newline text → string[] --------- */
      case 'bloodPoolROINames':
        newCriteria[key] = String(value || '')
          .split(/\r?\n/)
          .map(s => s.trim())
          .filter(s => s.length > 0);
        break;
      /* ------------------------------------------------ */
      default:
        newCriteria[key] = Number(value);
    }
    setCriteria(newCriteria);
  };

  const isRecip = criteria?.guidelineId === 'RECIP10';

  return (
    <Body>
      <Container mode={component === 'Viewer' ? 'dark' : 'light'}>
        {component === 'Platform' && (
          <Typography component="h2" variant="h6" color="primary" gutterBottom>
            Criteria
          </Typography>
        )}
        {component === 'Viewer' && (
          <>
            <Grid container spacing={0} sx={{ padding: '15px 0' }}>
              {[
                'ClinicalTrialSiteName',
                'ClinicalTrialSubjectID',
                'SliceThickness',
                'SpacingBetweenSlices',
              ].map(key => (
                <Grid item xs={6} key={key}>
                  <CriterionBox
                    criterionKey={key}
                    criterionValue={timepoint[key]}
                    onCriterionChange={() => {}}
                    readOnly={true}
                  ></CriterionBox>
                </Grid>
              ))}
            </Grid>
            <Divider></Divider>
          </>
        )}
        <Grid container spacing={0} sx={{ padding: '15px 0' }}>
          {['guidelineId', 'measurementTechnique'].map(key => (
            <Grid item xs={6} key={key}>
              <CriterionBox
                criterionKey={key}
                criterionValue={getCriterionValue(key, criteria[key])}
                onCriterionChange={onCriterionChange}
                readOnly={readOnly}
              ></CriterionBox>
            </Grid>
          ))}
        </Grid>
        <Divider></Divider>
        {/* RECIP has no target/non-target concept; hide the target-count block */}
        {!isRecip && (
          <>
            <Grid container spacing={0} sx={{ padding: '15px 0' }}>
              {['maxNumberOfTargets', 'maxNumberOfTargetsPerOrgan'].map(key => (
                <Grid item xs={6} key={key}>
                  <CriterionBox
                    criterionKey={key}
                    criterionValue={getCriterionValue(key, criteria[key])}
                    onCriterionChange={onCriterionChange}
                    readOnly={readOnly || criteria.guidelineId !== 'CUSTOM'}
                  ></CriterionBox>
                </Grid>
              ))}
            </Grid>
            <Divider></Divider>
          </>
        )}
        {criteria?.measurementTechnique === 'BIDIMENSIONAL' && (
          <Grid container spacing={0} sx={{ padding: '15px 0' }}>
            {[
              'measurableLongAxis',
              'measurableShortAxis',
              'measurableLongAxisOfNewLesion',
              'smallDiameterChange',
              'measurableLongAxisOfNewLesionHST',
              'highSliceThickness',
              'minDiameterIncrease',
              'absoluteDiameterChange',
              'minDiameterDecrease',
              'absoluteSumOfDiameterChange',
            ].map(key => (
              <Grid item xs={6} key={key}>
                <CriterionBox
                  criterionKey={key}
                  criterionValue={getCriterionValue(key, criteria[key])}
                  onCriterionChange={onCriterionChange}
                  readOnly={readOnly || criteria.guidelineId !== 'CUSTOM'}
                ></CriterionBox>
              </Grid>
            ))}
          </Grid>
        )}
        {criteria?.measurementTechnique === 'VOLUMETRIC' && (
          <Grid container spacing={0} sx={{ padding: '15px 0' }}>
            {[
              'measurableVolume',
              '',
              'minVolumeIncrease',
              '',
              'minVolumeDecrease',
            ].map((key, idx) => (
              <Grid item xs={6} key={key || `placeholder-${idx}`}>
                <CriterionBox
                  criterionKey={key}
                  criterionValue={getCriterionValue(key, criteria[key])}
                  onCriterionChange={onCriterionChange}
                  readOnly={
                    readOnly ||
                    (criteria.guidelineId !== 'CUSTOM' &&
                      criteria.guidelineId !== 'RECIP10')
                  }
                ></CriterionBox>
              </Grid>
            ))}
          </Grid>
        )}
        <Divider></Divider>
        {/* --------- RECIP-specific section --------- */}
        {isRecip && (
          <>
            <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
              RECIP 1.0 Specific Options
            </Typography>
            <Grid container spacing={0} sx={{ padding: '15px 0' }}>
              {['pdReference', 'newLesionAccounting'].map(key => (
                <Grid item xs={6} key={key}>
                  <CriterionBox
                    criterionKey={key}
                    criterionValue={getCriterionValue(key, criteria[key])}
                    onCriterionChange={onCriterionChange}
                    readOnly={readOnly}
                  ></CriterionBox>
                </Grid>
              ))}
            </Grid>
            {/* --------- NEW: blood-pool detection + borderline --------- */}
            <Typography
              variant="subtitle2"
              color="primary"
              sx={{ mt: 1, mb: 0.5 }}
            >
              PSMA(+) / (−) Classification
            </Typography>
            <Grid container spacing={0} sx={{ padding: '5px 0' }}>
              <Grid item xs={12}>
                <CriterionBox
                  criterionKey={'bloodPoolROINames'}
                  criterionValue={getCriterionValue(
                    'bloodPoolROINames',
                    criteria['bloodPoolROINames']
                  )}
                  onCriterionChange={onCriterionChange}
                  readOnly={readOnly}
                ></CriterionBox>
              </Grid>
              <Grid item xs={6}>
                <CriterionBox
                  criterionKey={'borderlineRatio'}
                  criterionValue={getCriterionValue(
                    'borderlineRatio',
                    criteria['borderlineRatio']
                  )}
                  onCriterionChange={onCriterionChange}
                  readOnly={readOnly}
                ></CriterionBox>
              </Grid>
            </Grid>
            <Divider></Divider>
            {/* NEW: SUV defaults panel */}
            <SuvDefaultsPanel />
          </>
        )}
        {/* ------------------------------------------------ */}
        <Grid container spacing={0} sx={{ padding: '15px 0' }}>
          {[
            'completeResponseConfirmation',
            'minStableDiseaseDuration',
            'newLesionConfirmation',
            'newLesionDateOfProgression',
          ].map(key => (
            <Grid item xs={6} key={key}>
              <CriterionBox
                criterionKey={key}
                criterionValue={getCriterionValue(key, criteria[key])}
                onCriterionChange={onCriterionChange}
                readOnly={readOnly || criteria.guidelineId !== 'CUSTOM'}
              ></CriterionBox>
            </Grid>
          ))}
        </Grid>
        <Divider></Divider>
        <Grid container spacing={0} sx={{ padding: '15px 0' }}>
          {['newLesionAlert', 'overallResponseAlert'].map(key => (
            <Grid item xs={6} key={key}>
              <CriterionBox
                criterionKey={key}
                criterionValue={getCriterionValue(key, criteria[key])}
                onCriterionChange={onCriterionChange}
              ></CriterionBox>
            </Grid>
          ))}
        </Grid>
        <Divider></Divider>
      </Container>
      <div>
        {!readOnly && component === 'Platform' && (
          <Box sx={{ margin: '15px 0 0 0' }}>
            <Button onClick={() => onUpdateCriteriaClick(criteria)} fullWidth>
              Update
            </Button>
          </Box>
        )}
        {!readOnly && component === 'Viewer' && (
          <Box sx={{ margin: '15px 0 0 0' }}>
            <Grid container spacing={0}>
              <Grid item xs={4}></Grid>
              <Grid item xs={4}></Grid>
              <Grid item xs={4}>
                <Button
                  onClick={() => onUpdateCriteriaClick(criteria)}
                  color="secondary"
                  variant="contained"
                  fullWidth
                >
                  Update
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </div>
    </Body>
  );
};

export default CriteriaForm;
